import { FC } from 'react';
import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { useAtomValue } from 'jotai';

import { Loader } from '@/Components/Loader.tsx';
import { useCheckScrollBarWidth } from 'Helpers/hooks/useCheckScrollBarWidth.ts';
import { analytics } from 'Services/mixpanel';
import { updateSentryUser } from 'Services/sentry';
import { appUserAtom } from 'State/app-user.ts';
import { auth0UserAtom } from 'State/auth0-user.ts';

if (import.meta.env.VITE_APP_ENVIRONMENT === 'development' || import.meta.env.DEV) {
    // Adds messages only in a dev environment
    loadDevMessages();
    loadErrorMessages();
}

type AppProps = object;
export const App: FC<AppProps> = () => {
    const auth0User = useAtomValue(auth0UserAtom);
    const appUser = useAtomValue(appUserAtom);

    useEffect(() => {
        if (!auth0User) return;
        updateSentryUser(auth0User && { id: auth0User.sub, email: auth0User.email });
        analytics.identify(auth0User?.sub);
        analytics.setUserProperties({
            $email: auth0User?.email,
        });
    }, [auth0User]);

    useEffect(() => {
        if (!appUser) return;
        const username = appUser.full_name;
        updateSentryUser({
            id: appUser.user_id,
            email: appUser.email,
            username,
        });
        analytics.setUserProperties({
            Role: appUser.role,
            ...(username && { $name: username }),
        });
    }, [appUser]);

    useCheckScrollBarWidth();

    return (
        <Suspense fallback={<Loader />}>
            <Outlet />
        </Suspense>
    );
};
